import { IGeneralErrorObject, ITicketActionResponse } from '../../../../types'
import {
  EFileBadgeStatus,
  EFileStatus,
  EMatchingPaymentTicketStatus,
  EMatchingPayoutTicketStatus,
  EMatchingStatus,
} from '../../../../types/enums'
import { TAmountChangeSource } from '../../../../components/AmountChangeBadge'
import { IsGranted, ROLE, UserModel } from '../../auth'
import { IBaseFilters } from '../../../common/filter/FilterManager'
import { OverpaymentHistoryItem } from '../routes/Overpayments/core/response.types'

export type PaginationState = {
  page: number
  perPage: number
}

export interface TicketActionResponse extends ITicketActionResponse<MatchingPaymentTicket> { }

// Ответ создания виртуального платежа
export interface VirtualMerchantPaymentResponse {
  success: boolean
  totalRows?: number
  paymentTicketToken?: string
  virtualPayoutTicketToken?: string
  errors?: {
    general_error?: string
  }
}

// Общие фильтры для тикетов
export interface IMatchingTicketsFilters {
  search?: string // мультипоиск по полям (id тикета + id транзакции + токен тикета)Z
  byCompletionDate: boolean // Переключатель диапазона дат (true - по дате завершения, false - по дате создания)
  dateStart: Date // Дата начала диапазона
  dateEnd: Date // Дата конца диапазона
}

// Общие параметры запроса для тикетов
export interface IMatchingTicketsRequestParams {
  search?: string
  statusList?: EMatchingPayoutTicketStatus[]
  merchantTokenList?: string[]
  paymentMethodList?: string[]
  dateStart?: string
  dateEnd?: string
  byCompletionDate?: boolean
}

export interface IMatchingFileResponse<T> {
  success: boolean
  mediaObject: T
  errors: Partial<IGeneralErrorObject>
  totalRows: number
}

export interface MatchingFileResponse extends IMatchingFileResponse<MatchingFile> { }

// CPAYMENT-1616: почему чек дошёл до оператора без подтверждения авто-проверкой.
// null — вопросов нет: тикет не проходил гейт либо проверка приняла чек.
export interface ReceiptGate {
  reason: 'unreadable' | 'requisite_mismatch' | 'no_verdict' | 'check_error'
  reuploads: number
}

// Файл, прикреплённый к тикету. Чеки приходят массивом: текущий первым, затем заменённые
// сотрудником (current === false), новые выше.
export interface MatchingFile {
  id: number
  url: string
  status: EFileStatus
  token?: string
  autoCheckStatus?: EFileBadgeStatus | null
  autoCheckMessage?: string | null
  fileName?: string
  // Нет поля — ответ первого прикрепления, это текущий чек.
  current?: boolean
  createdAt?: string | null
  replacedAt?: string | null
  // uuid сотрудника, заменившего чек; null у старой истории
  replacedBy?: string | null
}

// Реквизит платежа (структура по API, не совпадает с IRequisite)
export interface MatchingRequisite {
  identifier: string
  bank?: string | null
  cardholder?: string | null
}

// Антифрод-сигнал по тикету (CPAYMENT-1341)
export type AntifraudCheckType =
  | 'amount_mismatch'
  | 'requisite_mismatch'
  | 'time_mismatch'
  | 'duplicate'
  | 'submit_speed'
  | 'bank_mismatch'
  // Связь клиента с заблокированной учёткой — приходит не от антифрода чека,
  // а собирается бэкендом в том же сигнале.
  | 'client_scam_link'
  | 'client_open_dispute'
  | 'client_active_deposits'
  // Уровень риска сквозного клиента по его профилю; `info` бывает только у этой строки.
  | 'client_risk'

export type AntifraudColor = 'green' | 'yellow' | 'red' | 'black'
export type AntifraudLevel = 'yellow' | 'red' | 'info'

export interface AntifraudCheck {
  type: AntifraudCheckType
  level: AntifraudLevel
  triggered: boolean
  detail: string
}

export interface AntifraudSignal {
  color: AntifraudColor
  checks: AntifraudCheck[]
}

/**
 * Тикет пополнения
 */

export interface RelatedPayoutTicket {
  token: string
  amount: number | null
  status: string | null
  matchingStatus: string | null
  createdAt: string | null
  merchant: string | null
  merchantTransactionId: string | null
  // The payout is an overpayment compensation (an overpayment record points at it).
  isOverpaymentCompensation: boolean
}

// Тикет пополнения (payment)
// Статус диспута по тикету (CPAYMENT-1615)
export type DisputeTicketStatus = 'OPEN' | 'RESOLVED_SUCCESS' | 'RESOLVED_REJECT'

export interface MatchingPaymentTicket {
  ticketToken: string
  merchantId?: string | null
  merchantTransactionId?: string | null
  createdAt: string
  expectedAmount: number | null
  amount: number | null
  requisiteAmount?: number | null
  // CPAYMENT-1669: variant | customization — чем выставлена сумма к переводу.
  amountChangeSource?: TAmountChangeSource | null
  merchant: string
  clientId?: number | null
  externalUserId?: string | null
  // Токен клиента для ссылки на карточку /client/info/{token}; у старых ответов API его нет.
  clientToken?: string | null
  paymentMethod?: string | null
  // A concierge deposit: staff work it from the concierge window of the active ticket list.
  concierge?: boolean
  completionDate?: string | null
  status: EMatchingPaymentTicketStatus
  // Почему заявка завершилась. Статус говорит, насколько далеко она прошла, и один и тот же
  // EXPIRED прилетает и на отмену клиентом, и на истёкший срок — различает только причина.
  // Код нужен для отчётов, текст — сотруднику; оба приходят только у завершённых заявок.
  closeReason?: string | null
  closeReasonTitle?: string | null
  // Reason the client picked when cancelling on the payment page; manager/admin only.
  clientCancelReasonTitle?: string | null
  requisite?: MatchingRequisite | null
  files?: MatchingFile[] | null
  paymentTicketList?: PaymentTicket[]
  relatedPayoutTicket?: RelatedPayoutTicket | null
  matchingTransactionId?: number | null
  // Состояние переоплаты по этому депозиту (из payout/list)
  overpaymentInProgress?: boolean
  overpaymentCompensationToken?: string | null
  overpaymentCompensationStatus?: string | null
  // CPAYMENT-1835: запись переоплаты и признак «компенсационную выплату ещё можно провести через
  // локальный адаптер» — то же действие, что в журнале переоплат, вторая точка входа.
  overpaymentId?: number | null
  overpaymentCanPayViaLocalAdapter?: boolean
  antifraudSignal?: AntifraudSignal | null
  // CPAYMENT-1615: статус последнего диспута по тикету. OPEN — тикет заморожен (операции с чеками
  // недоступны); RESOLVED_* — принятое решение, показывается меткой в завершённых; null — не было.
  disputeStatus?: DisputeTicketStatus | null
  // CPAYMENT-1857: до какого момента спорный депозит держит свою выплату; null при открытом
  // споре — держим бессрочно. canReleaseChunk считает бэкенд по этой же паре.
  chunkHeldUntil?: string | null
  canReleaseChunk?: boolean
  // CPAYMENT-1616: причина, по которой чек ушёл к оператору без подтверждения авто-проверкой.
  receiptGate?: ReceiptGate | null
  // CPAYMENT-1712: логин сотрудника, обработавшего тикет (проверка чека либо ручное
  // завершение/отклонение). null — тикет прошёл без участия сотрудника.
  executor?: string | null
}

export interface PaymentTicket {
  ticketToken: string
  merchantId: string
  createdAt: string
  completionDate: string
  clientId: string
  externalUserId: string
  // Токен клиента депозита для ссылки на карточку /client/info/{token}.
  clientToken?: string | null
  merchant: string
  status: string
  authorId: number
  approximateAmount: string
  amount: string
  files: string[]
  matchingTransactionId?: number | null
  // Состояние переоплаты по этому депозиту (из payout/list)
  overpaymentInProgress?: boolean
  overpaymentCompensationToken?: string | null
  overpaymentCompensationStatus?: string | null
  // CPAYMENT-1835: запись переоплаты и признак «компенсационную выплату ещё можно провести через
  // локальный адаптер» — то же действие, что в журнале переоплат, вторая точка входа.
  overpaymentId?: number | null
  overpaymentCanPayViaLocalAdapter?: boolean
  // CPAYMENT-1801: статус последнего диспута по депозиту. OPEN — депозит заморожен и ёмкость
  // выплаты не занимает, поэтому в колонке «Кол-во матчей» у него своя корзина.
  disputeStatus?: DisputeTicketStatus | null
}

// Вложенный тикет пополнения внутри payout
export interface MatchingPayoutPaymentTicket extends MatchingPaymentTicket {
  authorId?: number | null
  approximateAmount?: number | null
  // Все переоплаты по депозиту, от новых к старым; у старых ответов API поля нет.
  overpaymentHistory?: OverpaymentHistoryItem[]
  // Выдача депозиту по этой выплате отменена (консьерж): выплата его больше не ждёт, а статус
  // депозита — уже не про неё.
  matchReleased?: boolean | null
}

// Ответ API для payment/list
export interface MatchingPaymentTicketListResponse {
  success: boolean
  totalRows: number
  list: MatchingPaymentTicket[]
  errors?: Partial<IGeneralErrorObject>
}

// Объекты для хранения списков
export interface MatchingPaymentTicketListObject {
  [token: string]: MatchingPaymentTicket
}

export interface MatchingPaymentActiveTicketsFilters {
  search?: string
}

export interface MatchingPaymentActiveTicketsRequestParams
  extends MatchingPaymentActiveTicketsFilters,
  PaginationState {
  statusList?: EMatchingPaymentTicketStatus[]
  // ... Добавлять по мере необходимости
}

// Тип фильтров для тикетов пополнения (payment)
export interface MatchingPaymentFinishedTicketsFilters {
  byCompletionDate: boolean // Переключатель диапазона дат (true - по дате завершения, false - по дате создания)
  dateStart: Date // Дата начала диапазона
  dateEnd: Date // Дата конца диапазона
  search?: string // мультипоиск по полям (id тикета + id транзакции + токен тикета)Z
  merchantTokenList?: string[] // Список мерчантов, по UUID
  // source?: string // Источник транзакции (PLATFORM, MERCHANT, SMS  и т.п.). Отключено в текущей реализации.
  statusList?: string[]
}

// Параметры запросов (swagger)
export interface MatchingPaymentFinishedTicketsRequestParams extends PaginationState {
  search?: string // мультипоиск по полям (id тикета + id транзакции + токен тикета)
  merchantTokenList?: string[] // Список мерчантов, по UUID
  paymentMethodList?: string[] // Список методов оплаты
  statusList?: string[]
  createdAtStart?: string
  createdAtEnd?: string
  completionDateStart?: string
  completionDateEnd?: string
  // ... Добавлять по мере необходимости
}

export function PrepareMatchingPaymentTicketsToObject(
  tickets: Array<MatchingPaymentTicket>
): MatchingPaymentTicketListObject {
  let object: MatchingPaymentTicketListObject = {}
  tickets.forEach((item) => {
    object[item.ticketToken] = item
  })
  return object
}

export function isMatchingPaymentTicketStatus(
  status: string
): EMatchingPaymentTicketStatus | undefined {
  return Object.values(EMatchingPaymentTicketStatus).includes(
    status as EMatchingPaymentTicketStatus
  )
    ? (status as EMatchingPaymentTicketStatus)
    : undefined
}

export type MatchingPaymentStatusOption = { value: EMatchingPaymentTicketStatus; label: string }

/**
 * @todo:
 * - Соотнести с интернационализацией, при необходимости расширить, использовать useIntl в компонентах.
 */
const MatchingPaymentStatusOptions: Record<
  EMatchingPaymentTicketStatus,
  MatchingPaymentStatusOption
> = {
  CREATED: {
    value: EMatchingPaymentTicketStatus.CREATED,
    label: 'Создан',
  },
  ACTIVE: {
    value: EMatchingPaymentTicketStatus.ACTIVE,
    label: 'Активен',
  },
  WAITING_FOR_REQUISITE: {
    value: EMatchingPaymentTicketStatus.WAITING_FOR_REQUISITE,
    label: 'Ждет реквизитов',
  },
  WAITING_FOR_SMS: {
    value: EMatchingPaymentTicketStatus.WAITING_FOR_SMS,
    label: 'Ждет СМС',
  },
  WAITING_FOR_MERCHANT: {
    value: EMatchingPaymentTicketStatus.WAITING_FOR_MERCHANT,
    label: 'Ждет мерчанта',
  },
  WAITING_FOR_ACTION: {
    value: EMatchingPaymentTicketStatus.WAITING_FOR_ACTION,
    label: 'Ждет действия',
  },
  CLIENT_PENDING: {
    value: EMatchingPaymentTicketStatus.CLIENT_PENDING,
    label: 'Ждет клиента',
  },
  ADAPTER_PENDING: {
    value: EMatchingPaymentTicketStatus.ADAPTER_PENDING,
    label: 'Ждет адаптера',
  },
  PAYMENT_PENDING: {
    value: EMatchingPaymentTicketStatus.PAYMENT_PENDING,
    label: 'Ждет платежа',
  },
  NOT_CONFIRMED: {
    value: EMatchingPaymentTicketStatus.NOT_CONFIRMED,
    label: 'Не подтвержден',
  },
  FILES_AUTO_VERIFICATION: {
    value: EMatchingPaymentTicketStatus.FILES_AUTO_VERIFICATION,
    label: 'Проверка платежа',
  },
  FILES_VERIFICATION: {
    value: EMatchingPaymentTicketStatus.FILES_VERIFICATION,
    label: 'Ждет файлов',
  },
  DECLINED: {
    value: EMatchingPaymentTicketStatus.DECLINED,
    label: 'Отклонен',
  },
  EXPIRED: {
    value: EMatchingPaymentTicketStatus.EXPIRED,
    label: 'Просрочен',
  },
  COMPLETED: {
    value: EMatchingPaymentTicketStatus.COMPLETED,
    label: 'Завершен',
  },
}

export function getFinishedPaymentStatusOptions(): Record<
  EMatchingPaymentTicketStatus,
  MatchingPaymentStatusOption
> {
  return MatchingPaymentStatusOptions
}

export function getMatchingPaymentStatusOption(
  status?: EMatchingPaymentTicketStatus | MatchingPaymentStatusOption | undefined
): MatchingPaymentStatusOption | undefined {
  const statusValue = typeof status === 'string' ? status : status?.value
  return statusValue
    ? MatchingPaymentStatusOptions?.[statusValue as EMatchingPaymentTicketStatus]
    : undefined
}

export type MatchingPayoutStatusOption = { value: EMatchingPayoutTicketStatus; label: string }

export const MatchingPayoutStatusOptions: Record<
  EMatchingPayoutTicketStatus,
  MatchingPayoutStatusOption
> = {
  CREATED: {
    value: EMatchingPayoutTicketStatus.CREATED,
    label: 'Создан',
  },
  ACTIVE: {
    value: EMatchingPayoutTicketStatus.ACTIVE,
    label: 'Активен',
  },
  PLACEMENT_PENDING: {
    value: EMatchingPayoutTicketStatus.PLACEMENT_PENDING,
    label: 'Ожидает размещения',
  },
  ADAPTER_PENDING: {
    value: EMatchingPayoutTicketStatus.ADAPTER_PENDING,
    label: 'Ждет адаптера',
  },
  ACTION_PENDING: {
    value: EMatchingPayoutTicketStatus.ACTION_PENDING,
    label: 'Ждет действия',
  },
  PAYOUT_PENDING: {
    value: EMatchingPayoutTicketStatus.PAYOUT_PENDING,
    label: 'Ждет выплаты',
  },
  MATCHING_PROCESSING: {
    value: EMatchingPayoutTicketStatus.MATCHING_PROCESSING,
    label: 'Обработка мэтчинга',
  },
  CONFIRMATION_PENDING: {
    value: EMatchingPayoutTicketStatus.CONFIRMATION_PENDING,
    label: 'Ждет подтверждения',
  },
  COMPLETED: {
    value: EMatchingPayoutTicketStatus.COMPLETED,
    label: 'Завершен',
  },
  DECLINED: {
    value: EMatchingPayoutTicketStatus.DECLINED,
    label: 'Отклонен',
  },
}

export type MatchingStatusOption = { value: EMatchingStatus; label: string }

export const MatchingStatusOptions: Record<EMatchingStatus, MatchingStatusOption> = {
  PROCESSING: {
    value: EMatchingStatus.PROCESSING,
    label: 'Обрабатывается',
  },
  HALTED: {
    value: EMatchingStatus.HALTED,
    label: 'В процессе остановки',
  },
  COMPLETED: {
    value: EMatchingStatus.COMPLETED,
    label: 'Завершён',
  },
  DECLINED: {
    value: EMatchingStatus.DECLINED,
    label: 'Отменён',
  },
}

/**
 * Тикет выплаты
 */

type Range = [number, number];
// Тикет выплаты (payout)
export interface MatchingPayoutTicket {
  ticketToken: string
  merchantId?: string | null
  merchantTransactionId?: string | null
  merchant: string
  createdAt: string
  completionDate?: string | null
  matchCount: number
  // Занятые слоты чанков и лимит из снапшота конфига выплаты. Считает бэкенд —
  // «живой» депозит требует проверки внешней транзакции матчинг-адаптера,
  // которой в paymentTicketList нет. Лимит null, если снапшота у выплаты нет.
  liveMatchCount?: number | null
  maxMatchCount?: number | null
  matchingRemainder: number
  depositRange?: Range[] | null
  paymentMethod: string
  expectedAmount: number | null
  amount: number | null
  clientId: number
  externalUserId: string
  // Токен клиента для ссылки на карточку /client/info/{token}; у старых ответов API его нет.
  clientToken?: string | null
  status: EMatchingPayoutTicketStatus
  matchingStatus?: EMatchingStatus
  /**
   * Выплата на паузе: остаётся в мэтчинге, но новые пополнения на неё не назначаются. Поля нет в
   * ответах, которые паузу не отдают, — там кнопка паузы не показывается.
   */
  matchingPaused?: boolean
  matchingTimeoutAt?: string | null
  paymentTicketList: MatchingPayoutPaymentTicket[]
  recreatedToken?: string | null
  /** Первичный канал (копия в matching_ticket). Для составной выплаты неполон — см. requisites. */
  matchingTicketRequisite?: string | null
  matchingTicketCardholder?: string | null
  matchingTicketBank?: string | null
  /** CPAYMENT-1713: все каналы выплаты в порядке привязки. */
  requisites?: MatchingPayoutRequisite[] | null
  /** Статус последнего спора о выплате; null — споров не было. */
  payoutDisputeStatus?: DisputeTicketStatus | null
  /** Выплата завершена, есть оплаченный депозит, открытого спора о ней нет — правило бэкенда. */
  canOpenDispute?: boolean
  /** Halted but still open: the deposits matching waits for. Empty for a payout that is not halted. */
  haltHolds?: MatchingPayoutHaltHold[]
  /** Halted, yet no deposit holds it. */
  haltUndetermined?: boolean
}

/** Why a halted payout is still open — the backend's codes; wording lives in core/haltHolds. */
export type HaltHoldReason = 'DISPUTE' | 'FILES_VERIFICATION' | 'AWAITING_PAYMENT' | 'UNDETERMINED'

/** A deposit that keeps a halted payout waiting. */
export interface MatchingPayoutHaltHold {
  depositToken: string
  /** A code this build does not know reads as undetermined. */
  reason: HaltHoldReason | string
  /** DISPUTE only: when the payout is freed on its own (MSK ISO); null — indefinitely. */
  releaseAt?: string | null
  /** The deposit's ticket status — decides which page can show it. */
  depositStatus?: string
}

/** Вид реквизита — значения App\PaymentRequest\Domain\Entity\RequisiteType. */
export type RequisiteKind =
  | 'bank_card'
  | 'phone_number'
  | 'bank_account'
  | 'crypto_wallet'
  | 'redirect_url'
  | 'wallet'

/** Канал выплаты: один реквизит клиента, привязанный к тикету. */
export interface MatchingPayoutRequisite {
  /** Токен реквизита клиента — им адресуется канал при смене реквизита (CPAYMENT-1733). */
  token: string
  kind?: RequisiteKind
  identifier: string
  bank?: string | null
  cardholder?: string | null
  paymentMethod?: string | null
}

/** Реквизит клиента, которым можно заменить канал выплаты (CPAYMENT-1733). */
export interface PayoutRequisiteOption {
  token: string
  identifier: string
  bank?: string | null
  cardholder?: string | null
  paymentMethod: string
}

/**
 * Тело смены реквизита выплаты: либо токен уже существующего реквизита клиента, либо поля нового.
 * Платёжный метод не передаётся — бэкенд берёт его у заменяемого канала, иначе оператор мог бы
 * руками увести выплату на другой метод и выбить её из кандидатов.
 */
export interface ReplacePayoutRequisiteBody {
  channelToken: string
  requisiteToken?: string
  identifier?: string
  cardHolder?: string
  acquiringBank?: string
  comment?: string
}

export const REQUISITE_KIND_LABELS: Record<RequisiteKind, string> = {
  bank_card: 'Карта',
  phone_number: 'Телефон (СБП)',
  bank_account: 'Счёт',
  crypto_wallet: 'Криптокошелёк',
  redirect_url: 'Ссылка',
  wallet: 'Кошелёк (телефон)',
}

// Ответ API для payout/list
export interface MatchingPayoutTicketListResponse {
  success: boolean
  totalRows: number
  list: MatchingPayoutTicket[]
  errors?: Partial<IGeneralErrorObject>
}

export interface MatchingPayoutTicketListObject {
  [token: string]: MatchingPayoutTicket
}

export interface MatchingSummary {
  totalPayoutAmount: number
  remainderExcludingProcessing: number
  remainderIncludingProcessing: number
  activeCount: number
  currency: string
}

export interface MatchingSummaryResponse {
  success: boolean
  totalRows?: number
  data: MatchingSummary[] | null
}

export function PrepareMatchingPayoutTicketsToObject(
  tickets: Array<MatchingPayoutTicket>
): MatchingPayoutTicketListObject {
  let object: MatchingPayoutTicketListObject = {}
  tickets.forEach((item) => {
    object[item.ticketToken] = item
  })
  return object
}

/**
 * Состояние формы фильтров активных выплат (FilterManager).
 * search — из IBaseFilters; ниже — доменные мульти-фильтры.
 */
export interface MatchingPayoutActiveTicketsFilters extends IBaseFilters {
  merchantTokenList?: string[] // токены мерчантов
  paymentMethodList?: string[] // типы платёжных методов
}

export interface MatchingPayoutActiveTicketsRequestParams extends PaginationState {
  search?: string
  statusList?: EMatchingPayoutTicketStatus[]
  merchantTokenList?: string[]
  paymentMethodList?: string[]
  createdAtStart?: string
  createdAtEnd?: string
  // ... Добавлять по мере необходимости
}


// Параметры запроса для тикетов завершенных выплат (payout)
export interface IMatchingPayoutFinishedTicketsRequestParams extends PaginationState, IMatchingTicketsRequestParams {
  // ... Добавлять по мере необходимости
}

// Параметры запросов (swagger)
export interface MatchingPayoutFinishedTicketsRequestParams extends PaginationState {
  search?: string
  statusList?: string[]
  merchantTokenList?: string[]
  paymentMethodList?: string[]
  createdAtStart?: string
  createdAtEnd?: string
  completionDateStart?: string
  completionDateEnd?: string
  byCompletionDate?: boolean
  dateStart?: string
  dateEnd?: string
  csvExport?: boolean
  // ... Добавлять по мере необходимости
}

/**
 * Состояние формы фильтров завершённых выплат (FilterManager).
 * search / dateRange — из IBaseFilters; ниже — доменные мульти-фильтры.
 */
export interface MatchingFinishedPayoutFilterState extends IBaseFilters {
  statusList?: string[] // COMPLETED / DECLINED
  merchantTokenList?: string[] // токены мерчантов
  paymentMethodList?: string[] // типы платёжных методов
}

/**
 * Доступ к тикетам и операциям
 */

const canViewTickets = [ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_MATCHING_OPERATOR, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER]

const canViewActivePayments = [...canViewTickets]

const canViewFinishedPayments = [...canViewTickets]

const canViewActivePayouts = [...canViewTickets]

const canViewFinishedPayouts = [...canViewTickets]

/** Отказы мэтчинга по выплатам — роли matching-списков выплат, совпадают с бэкендом. */
const canViewPayoutRefusals = [ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_MATCHING_OPERATOR, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER]

const canViewAmount = [ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_MATCHING_OPERATOR, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER]

const canViewMerchant = [ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER]

const canViewRequisite = [ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_MATCHING_OPERATOR, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER]

const canEditTickets = [ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_MATCHING_OPERATOR, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER]

// CPAYMENT-1750: откат зачисленного депозита (COMPLETED → EXPIRED) — отдельное право, у оператора
// мэтчинга его нет. Список обязан совпадать с ролями перехода T_DECLINED / P_COMPLETED → P_EXPIRED
// в бэкендовом PaymentTicket/Domain/Workflow/Config.php. Из canEditTickets роль убирать нельзя —
// им же гейтятся verify/reject чека, complete и change-amount, которые ей разрешены.
const canRollbackCompleted = [
  ROLE.ROLE_ADMIN,
  ROLE.ROLE_MANAGER,
  ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR,
  ROLE.ROLE_MERCHANT_MANAGER,
]

// CPAYMENT-1615: список обязан совпадать с бэкендом — там DisputeController::MODULE_ROLES плюс
// правило в security.yaml. ROLE_MATCHING_OPERATOR наследует только ROLE_USER и модуль ему не
// открыт — кнопку не показываем, хотя чеки он верифицировать может. ROLE_MERCHANT_MANAGER добавлен
// в CPAYMENT-1732: он идёт в паре с менеджером ПС, на бэкенде его выборка сужена своими мерчантами.
const canCreateDispute = [
  ROLE.ROLE_ADMIN,
  ROLE.ROLE_MANAGER,
  ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR,
  ROLE.ROLE_MERCHANT_MANAGER,
]

// CPAYMENT-1726: список обязан совпадать с бэкендом — там IsGranted('ROLE_MANAGER' or
// 'ROLE_MERCHANT_PAYMENTS_OPERATOR') на POST /api/internal/overpayment и на lookup причин плюс
// правила в security.yaml. Это признак «можно создать переоплату из выплаты», а НЕ доступ к
// журналу /matching/overpayments (overpaymentJournalRoles в MatchingPage.tsx) и к настройкам
// причин — справочник остаётся за админом и менеджером. Бэкенд к тому же сужает создание своими
// мерчантами (по мерчанту ВЫПЛАТЫ), поэтому кнопка у роли видна только на её же выплатах.
const canCreateOverpayment = [ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR]

// Провести зависшую в мэтчинге переоплату через локальный адаптер на выбранной кассе
// (CPAYMENT-1835). Список обязан совпадать с бэкендом — там IsGranted('ROLE_MANAGER' or
// 'ROLE_MERCHANT_MANAGER') на POST /api/internal/overpayment/{id}/pay-via-local-adapter плюс
// правило в security.yaml. Право ОТДЕЛЬНОЕ от canCreateOverpayment: оператор платежей мерчанта
// переоплату заводит и журнал видит, но кассу не выбирает — состав ролей действия зафиксирован
// заказчиком.
const canPayOverpaymentViaLocalAdapter = [
  ROLE.ROLE_ADMIN,
  ROLE.ROLE_MANAGER,
  ROLE.ROLE_MERCHANT_MANAGER,
]

// Ручная переотправка КБ. Список обязан совпадать с бэкендом — там IsGranted('ROLE_MANAGER' or
// 'ROLE_MERCHANT_PAYMENTS_OPERATOR') на POST /api/internal/callback/resend плюс правило в
// security.yaml. Право отдельное от canEditTickets: оператор мэтчинга кнопку не видит, хотя
// тикеты редактировать может. Бэкенд сужает отправку своими мерчантами (по мерчанту САМОГО
// тикета), поэтому у роли кнопка срабатывает ровно на тех строках, которые ей и видны.
const canResendCallback = [ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR]

// «Изменить сумму зачисления» on a completed deposit. Must match the backend: CreditedAmountController::ROLES
// plus the rule in security.yaml; the scoped roles act on their own merchants only.
const canChangeCreditedAmount = [
  ROLE.ROLE_ADMIN,
  ROLE.ROLE_MANAGER,
  ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR,
  ROLE.ROLE_MERCHANT_MANAGER,
]

export const matchingCapabilities: Record<string, ROLE[]> = {
  canViewActivePayments,
  canViewActivePayouts,
  canViewAmount,
  canViewFinishedPayouts,
  canViewPayoutRefusals,
  canViewFinishedPayments,
  canViewMerchant,
  canViewRequisite,
  canEditTickets,
  canRollbackCompleted,
  canCreateDispute,
  canCreateOverpayment,
  canPayOverpaymentViaLocalAdapter,
  canResendCallback,
  canChangeCreditedAmount,
}

export const canUser = (user?: UserModel | null, allowedRoles?: ROLE[] | null): boolean => {
  if (!user || !allowedRoles?.length) return false
  return allowedRoles.some((role: ROLE) => user.roles?.includes(role))
}

export type ColumnVisibility = Record<string, boolean>

export const getVisibleActivePaymentColumns = (currentUser: any): ColumnVisibility => {
  return {
    ticketToken: true,
    transactionId: true,
    createdAt: true,
    amount: IsGranted(matchingCapabilities.canViewAmount, currentUser),
    merchant: IsGranted(matchingCapabilities.canViewMerchant, currentUser),
    paymentMethod: IsGranted(matchingCapabilities.canViewRequisite, currentUser),
    user: true,
    files: true,
    antifraud: true,
    status: true,
  }
}

export const getVisibleFinishedPaymentColumns = (currentUser: any): ColumnVisibility => {
  return {
    ticketToken: true,
    amount: IsGranted(matchingCapabilities.canViewAmount, currentUser),
    paymentMethod: IsGranted(matchingCapabilities.canViewRequisite, currentUser),
    createdDate: true,
    completionDate: true,
    client: true,
    merchant: IsGranted(matchingCapabilities.canViewMerchant, currentUser),
    requisite: IsGranted(matchingCapabilities.canViewRequisite, currentUser),
    file: IsGranted(matchingCapabilities.canViewRequisite, currentUser),
    antifraud: true,
    // CPAYMENT-1712: показываем всем, кто вообще открывает список — бэкенд отдаёт executor
    // ровно тем же ролям, что гейтят маршрут (менеджер + операторские роли мерчанта).
    executor: true,
    status: true,
  }
}

export const getVisibleActivePayoutColumns = (currentUser: any): ColumnVisibility => {
  return {
    ticketToken: true,
    merchantId: IsGranted(matchingCapabilities.canViewMerchant, currentUser),
    merchant: IsGranted(matchingCapabilities.canViewMerchant, currentUser),
    amount: IsGranted(matchingCapabilities.canViewAmount, currentUser),
    createdAt: true,
    matchCount: true,
    matchingRemainder: true,
    depositRange: true,
    paymentMethod: IsGranted(matchingCapabilities.canViewRequisite, currentUser),
    user: true,
    file: true,
    paymentTicketList: IsGranted(matchingCapabilities.canEditTickets, currentUser),
    status: true,
    matchingStatus: true,
    authorId: true,
    createdDate: true,
    completionDate: true,
    requisites: IsGranted(matchingCapabilities.canViewRequisite, currentUser),
  }
}

export const getVisibleFinishedPayoutColumns = (currentUser: any): ColumnVisibility => {
  return {
    ticketToken: true,
    amount: IsGranted(matchingCapabilities.canViewAmount, currentUser),
    paymentMethod: IsGranted(matchingCapabilities.canViewRequisite, currentUser),
    createdDate: true,
    completionDate: true,
    user: true,
    merchantId: IsGranted(matchingCapabilities.canViewMerchant, currentUser),
    merchant: IsGranted(matchingCapabilities.canViewMerchant, currentUser),
    payoutRequisite: IsGranted(matchingCapabilities.canViewRequisite, currentUser),
    file: IsGranted(matchingCapabilities.canViewRequisite, currentUser),
    status: true,
    paymentTicketList: IsGranted(canViewTickets, currentUser),
    authorId: IsGranted(canViewTickets, currentUser),
  }
}
